import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, Loader2, Plus } from 'lucide-react';
import useOperationStore from '../../store/useOperationStore';
import useAuthStore from '../../store/authStore';

const statuses = ['all', 'Pending', 'In_Transit', 'Customs', 'Delivered', 'Cancelled'];

const statusLabels = {
  all: 'Hepsi',
  Pending: 'Pending',
  In_Transit: 'In Transit',
  Customs: 'Customs',
  Delivered: 'Delivered',
  Cancelled: 'Cancelled'
};

const statusFilterLabels = {
  all: 'Tüm Durumlar',
  Pending: 'Beklemede (Pending)',
  In_Transit: 'Yolda / Sevkıyatta (In Transit)',
  Customs: 'Gümrükte (Customs)',
  Delivered: 'Teslim Edildi (Delivered)',
  Cancelled: 'İptal Edildi (Cancelled)'
};

const operationTypeOptions = [
  { value: 'all', label: 'Tüm Tipler' },
  { value: 'Import', label: 'İthalat (Import)' },
  { value: 'Export', label: 'İhracat (Export)' },
  { value: 'Inland', label: 'İç Nakliye (Inland)' },
  { value: 'None', label: 'Hiçbiri (None)' }
];

const transportModeOptions = [
  { value: 'all', label: 'Tüm Modlar' },
  { value: 'Air', label: 'Hava Yolu (Air)' },
  { value: 'Sea', label: 'Deniz Yolu (Sea)' },
  { value: 'Land', label: 'Kara Yolu (Land)' },
  { value: 'None', label: 'Hiçbiri (None)' }
];

const statusClass = {
  Pending: 'bg-amber-50 text-amber-700 border-amber-100',
  In_Transit: 'bg-blue-50 text-blue-700 border-blue-100',
  Customs: 'bg-purple-50 text-purple-700 border-purple-100',
  Delivered: 'bg-emerald-50 text-emerald-700 border-emerald-100',
  Cancelled: 'bg-rose-50 text-rose-700 border-rose-100'
};

const formatMeasuredM3 = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue.toFixed(2) : '0.00';
};

const OperationManagement = () => {
  const navigate = useNavigate();
  const {
    allOperations,
    loading,
    fetchAllOperations
  } = useOperationStore();
  const hasPermission = useAuthStore((state) => state.hasPermission);
  const canWriteOperation = hasPermission('operation', 'write');

  const [activeStatus, setActiveStatus] = useState('all');
  const [operationTypeFilter, setOperationTypeFilter] = useState('all');
  const [transportModeFilter, setTransportModeFilter] = useState('all');
  const [viewType, setViewType] = useState('all');
  const [accountSearch, setAccountSearch] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const safeOperations = Array.isArray(allOperations) ? allOperations : [];

  useEffect(() => {
    const trimmedSearch = accountSearch.trim();
    const filters = {
      viewType,
      ...(activeStatus !== 'all' ? { status: activeStatus } : {}),
      ...(operationTypeFilter !== 'all' ? { operationType: operationTypeFilter } : {}),
      ...(transportModeFilter !== 'all' ? { transportMode: transportModeFilter } : {}),
      ...(trimmedSearch ? { search: trimmedSearch, accountName: trimmedSearch } : {}),
      ...(startDate ? { startDate } : {}),
      ...(endDate ? { endDate } : {})
    };

    fetchAllOperations(filters);
  }, [
    activeStatus,
    operationTypeFilter,
    transportModeFilter,
    viewType,
    accountSearch,
    startDate,
    endDate,
    fetchAllOperations
  ]);

  const openOperationCreate = () => {
    window.open('/operations/new', '_blank', 'noopener,noreferrer');
  };

  const openOperationDetail = (operationId) => {
    navigate(`/operations/${operationId}`);
  };

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-6">
      <div className="flex flex-col justify-between gap-4 rounded-[2rem] border border-slate-100 bg-white p-5 shadow-sm xl:flex-row xl:items-end">
        <div>
          <h1 className="text-2xl font-black text-slate-900">Operasyon Yönetimi</h1>
          <p className="mt-1 text-sm font-bold text-slate-500">Merkezi lojistik kontrol kulesi ve operasyon listesi</p>
        </div>
        {canWriteOperation && (
          <button
            type="button"
            onClick={openOperationCreate}
            className="flex items-center justify-center gap-2 rounded-2xl bg-slate-900 px-6 py-3 text-xs font-black uppercase tracking-widest text-white hover:bg-black"
          >
            <Plus size={18} /> Yeni Operasyon
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-3 rounded-[1.5rem] border border-slate-100 bg-white p-4 shadow-sm">
        <div className="min-w-[220px] flex-1">
          <label className="mb-1.5 block text-[10px] font-black uppercase tracking-widest text-slate-400">
            Görünüm
          </label>
          <select
            value={viewType}
            onChange={(e) => setViewType(e.target.value)}
            className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs font-black text-slate-700 outline-none transition-all focus:border-blue-200 focus:bg-white focus:ring-4 focus:ring-blue-50"
          >
            <option value="all">Tümü</option>
            <option value="my">Benimkiler</option>
            <option value="tracked">Takip Ettiklerim</option>
          </select>
        </div>

        <div className="min-w-[240px] flex-1">
          <label className="mb-1.5 block text-[10px] font-black uppercase tracking-widest text-slate-400">
            Müşteri Ara...
          </label>
          <input
            type="text"
            value={accountSearch}
            onChange={(e) => setAccountSearch(e.target.value)}
            placeholder="Cari adı ile arayın"
            className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs font-black text-slate-700 outline-none transition-all placeholder:text-slate-300 focus:border-blue-200 focus:bg-white focus:ring-4 focus:ring-blue-50"
          />
        </div>

        <div className="min-w-[180px] flex-1">
          <label className="mb-1.5 block text-[10px] font-black uppercase tracking-widest text-slate-400">
            Başlangıç Tarihi
          </label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs font-black text-slate-700 outline-none transition-all focus:border-blue-200 focus:bg-white focus:ring-4 focus:ring-blue-50"
          />
        </div>

        <div className="min-w-[180px] flex-1">
          <label className="mb-1.5 block text-[10px] font-black uppercase tracking-widest text-slate-400">
            Bitiş Tarihi
          </label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs font-black text-slate-700 outline-none transition-all focus:border-blue-200 focus:bg-white focus:ring-4 focus:ring-blue-50"
          />
        </div>

        <div className="min-w-[220px] flex-1">
          <label className="mb-1.5 block text-[10px] font-black uppercase tracking-widest text-slate-400">
            Durum
          </label>
          <select
            value={activeStatus}
            onChange={(e) => setActiveStatus(e.target.value)}
            className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs font-black text-slate-700 outline-none transition-all focus:border-blue-200 focus:bg-white focus:ring-4 focus:ring-blue-50"
          >
            {statuses.map((status) => (
              <option key={status} value={status}>
                {statusFilterLabels[status]}
              </option>
            ))}
          </select>
        </div>

        <div className="min-w-[220px] flex-1">
          <label className="mb-1.5 block text-[10px] font-black uppercase tracking-widest text-slate-400">
            Operasyon Tipi
          </label>
          <select
            value={operationTypeFilter}
            onChange={(e) => setOperationTypeFilter(e.target.value)}
            className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs font-black text-slate-700 outline-none transition-all focus:border-blue-200 focus:bg-white focus:ring-4 focus:ring-blue-50"
          >
            {operationTypeOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="min-w-[220px] flex-1">
          <label className="mb-1.5 block text-[10px] font-black uppercase tracking-widest text-slate-400">
            Taşıma Modu
          </label>
          <select
            value={transportModeFilter}
            onChange={(e) => setTransportModeFilter(e.target.value)}
            className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs font-black text-slate-700 outline-none transition-all focus:border-blue-200 focus:bg-white focus:ring-4 focus:ring-blue-50"
          >
            {transportModeOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="relative overflow-hidden rounded-[2.5rem] border border-slate-100 bg-white shadow-sm">
        {loading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60">
            <Loader2 className="animate-spin text-blue-600" size={34} />
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-slate-100 text-[10px] font-black uppercase tracking-widest text-slate-400">
                <th className="p-5">Operasyon No</th>
                <th className="p-5">Cari</th>
                <th className="p-5">Eksper</th>
                <th className="p-5">m³</th>
                <th className="p-5">Hizmet</th>
                <th className="p-5">Durum</th>
                <th className="p-5">Evrak</th>
                <th className="p-5 text-right">İşlem</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {safeOperations.map((operation) => (
                <tr
                  key={operation._id}
                  onClick={() => openOperationDetail(operation._id)}
                  className="cursor-pointer transition-colors hover:bg-slate-50"
                >
                  <td className="p-5">
                    <div className="font-black text-slate-800">{operation.projectNo}</div>
                    <div className="text-[10px] font-bold text-slate-400">
                      {new Date(operation.createdAt).toLocaleDateString('tr-TR')}
                    </div>
                  </td>
                  <td className="p-5 font-bold text-slate-600">{operation.account?.name || 'Bağımsız Operasyon'}</td>
                  <td className="p-5">
                    <div className="font-bold text-slate-700">
                      {operation.estimator?.name || operation.estimator?.username || '-'}
                    </div>
                    <div className="text-[10px] font-bold text-slate-400">
                      {operation.estimator?.email || ''}
                    </div>
                  </td>
                  <td className="p-5 font-black text-slate-700">{formatMeasuredM3(operation.measuredM3)} m³</td>
                  <td className="p-5">
                    <div className="font-bold text-slate-700">{operation.serviceType}</div>
                    <div className="text-[10px] font-bold text-slate-400">
                      {operation.operationType} / {operation.transportMode}
                    </div>
                  </td>
                  <td className="p-5">
                    <span className={`inline-flex rounded-lg border px-3 py-1 text-[10px] font-black ${statusClass[operation.status] || 'border-slate-100 bg-slate-50 text-slate-600'}`}>
                      {statusLabels[operation.status] || operation.status}
                    </span>
                  </td>
                  <td className="p-5">
                    {operation.requiredDocumentsStatus === 'Missing' ? (
                      <span className="inline-flex items-center gap-1 text-xs font-black text-rose-600">
                        <AlertTriangle size={15} /> Missing
                      </span>
                    ) : operation.requiredDocumentsStatus === 'Complete' ? (
                      <span className="inline-flex items-center gap-1 text-xs font-black text-emerald-600">
                        <CheckCircle2 size={15} /> Complete
                      </span>
                    ) : (
                      <span className="text-xs font-black text-slate-400">None</span>
                    )}
                  </td>
                  <td className="p-5 text-right">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        openOperationDetail(operation._id);
                      }}
                      className="rounded-2xl bg-blue-600 px-4 py-2 text-xs font-black text-white transition-colors hover:bg-blue-700"
                    >
                      Detay
                    </button>
                  </td>
                </tr>
              ))}
              {safeOperations.length === 0 && (
                <tr>
                  <td colSpan="8" className="py-16 text-center font-bold text-slate-400">
                    Operasyon bulunamadı.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default OperationManagement;
